import React, { useEffect, useState } from 'react';
import { Table, Button, Tag, Alert, Card, Input, Select, Space, Modal, Typography, Row, Col, Statistic, Descriptions, message } from 'antd';
import { UserOutlined, SearchOutlined, EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, MessageOutlined, EditOutlined } from '@ant-design/icons';
import api from '../../../services/api';
import { getStatusColor } from '../../../utils/ui';
import pusherService from '../../../services/pusher';
import LeadEditModal from '../Vendor/LeadEditModal';

function AdminLeads() {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [viewingLead, setViewingLead] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingLead, setEditingLead] = useState(null);
    const [editModalVisible, setEditModalVisible] = useState(false);

    useEffect(() => {
        loadLeads();
        
        // Set up Pusher notifications for new leads
        const setupPusher = async () => {
            try {
                await pusherService.onNewLead((data) => {
                    console.log('New lead received:', data);
                    message.success('New booking request received!');
                    loadLeads(); // Refresh the leads list
                });
            } catch (error) {
                console.error('Failed to setup Pusher notifications:', error);
            }
        };
        
        setupPusher();
    }, []);

    const loadLeads = async () => {
        try {
            setLoading(true);
            const res = await api.adminGetLeads({ per_page: 100 });
            setLeads(res.data.data || []);
        } catch (e) {
            setError('Failed to load leads');
        } finally {
            setLoading(false);
        }
    };

    const updateLeadStatus = async (leadId, status) => {
        try {
            await api.adminUpdateLead(leadId, { status });
            message.success('Lead status updated successfully');
            loadLeads();
        } catch (e) {
            message.error('Failed to update lead status');
        }
    };

    const editLead = (lead) => {
        setEditingLead(lead);
        setEditModalVisible(true);
    };

    const handleLeadSave = (updatedLead) => {
        setEditModalVisible(false);
        setEditingLead(null);
        loadLeads(); // Refresh the list
    };

    const handleSendQuote = (quotedLead) => {
        setEditModalVisible(false);
        setEditingLead(null);
        loadLeads(); // Refresh the list
        message.success('Quote sent successfully!');
    };

    const viewLeadDetails = (lead) => {
        setViewingLead(lead);
        setModalVisible(true);
    };

    const statusColor = (status) => getStatusColor(status);

    const filteredLeads = leads.filter(lead => {
        const matchesSearch = lead.user?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
                            lead.vendor?.business_name?.toLowerCase().includes(searchText.toLowerCase()) ||
                            lead.event_type?.toLowerCase().includes(searchText.toLowerCase());
        const matchesStatus = !statusFilter || lead.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const columns = [
        {
            title: 'Lead ID',
            dataIndex: 'id',
            key: 'id',
            render: (id) => (
                <Typography.Text code>#{id}</Typography.Text>
            )
        },
        {
            title: 'Customer',
            key: 'customer',
            render: (_, lead) => (
                <div>
                    <div className="font-medium">{lead.user?.name}</div>
                    <div className="text-sm text-gray-500">{lead.user?.email}</div>
                </div>
            )
        },
        {
            title: 'Vendor',
            dataIndex: ['vendor', 'business_name'],
            key: 'vendor'
        },
        {
            title: 'Event Type',
            dataIndex: 'event_type',
            key: 'event_type',
            render: (type) => (
                <Tag color="blue">{type}</Tag>
            )
        },
        {
            title: 'Event Date',
            dataIndex: 'event_date',
            key: 'event_date'
        },
        {
            title: 'Guests',
            dataIndex: 'guest_count',
            key: 'guests'
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={statusColor(status)}>
                    {status?.charAt(0).toUpperCase() + status?.slice(1)}
                </Tag>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, lead) => (
                <Space>
                    <Button 
                        size="small" 
                        icon={<EyeOutlined />}
                        onClick={() => viewLeadDetails(lead)}
                    >
                        View
                    </Button>
                    <Button 
                        size="small" 
                        icon={<EditOutlined />}
                        onClick={() => editLead(lead)}
                        type="primary"
                    >
                        Edit
                    </Button>
                    {lead.status === 'pending' && (
                        <>
                            <Button 
                                type="primary" 
                                size="small" 
                                icon={<CheckCircleOutlined />}
                                onClick={() => updateLeadStatus(lead.id, 'quoted')}
                            >
                                Quote
                            </Button>
                            <Button 
                                danger 
                                size="small" 
                                icon={<CloseCircleOutlined />}
                                onClick={() => updateLeadStatus(lead.id, 'rejected')}
                            >
                                Reject
                            </Button>
                        </>
                    )}
                </Space>
            )
        }
    ];

    const stats = {
        totalLeads: leads.length,
        pendingLeads: leads.filter(l => l.status === 'pending').length,
        quotedLeads: leads.filter(l => l.status === 'quoted').length,
        acceptedLeads: leads.filter(l => l.status === 'accepted').length
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <Typography.Title level={2} className="!mb-0">Lead Management</Typography.Title>
                            <Typography.Text type="secondary">Manage customer booking requests and leads</Typography.Text>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto p-6">
                {error && <Alert type="error" message={error} className="mb-6" />}

                {/* Stats */}
                <Row gutter={[16, 16]} className="mb-6">
                    <Col xs={24} sm={6}>
                        <Card>
                            <Statistic
                                title="Total Leads"
                                value={stats.totalLeads}
                                prefix={<UserOutlined />}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={6}>
                        <Card>
                            <Statistic
                                title="Pending"
                                value={stats.pendingLeads}
                                prefix={<ClockCircleOutlined />}
                                valueStyle={{ color: '#fa8c16' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={6}>
                        <Card>
                            <Statistic
                                title="Quoted"
                                value={stats.quotedLeads}
                                prefix={<MessageOutlined />}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={6}>
                        <Card>
                            <Statistic
                                title="Accepted"
                                value={stats.acceptedLeads}
                                prefix={<CheckCircleOutlined />}
                                valueStyle={{ color: '#52c41a' }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Filters */}
                <Card className="mb-6">
                    <Row gutter={[16, 16]} align="middle">
                        <Col xs={24} sm={12} md={8}>
                            <Input
                                placeholder="Search leads..."
                                prefix={<SearchOutlined />}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Select
                                placeholder="Filter by status"
                                value={statusFilter}
                                onChange={setStatusFilter}
                                style={{ width: '100%' }}
                                allowClear
                            >
                                <Select.Option value="pending">Pending</Select.Option>
                                <Select.Option value="quoted">Quoted</Select.Option>
                                <Select.Option value="accepted">Accepted</Select.Option>
                                <Select.Option value="rejected">Rejected</Select.Option>
                                <Select.Option value="completed">Completed</Select.Option>
                            </Select>
                        </Col>
                    </Row>
                </Card>

                {/* Leads Table */}
                <Card>
                    <Table
                        columns={columns}
                        dataSource={filteredLeads}
                        rowKey="id"
                        loading={loading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} leads`
                        }}
                        scroll={{ x: true }}
                    />
                </Card>

                {/* Lead Details Modal */}
                <Modal
                    title="Lead Details"
                    open={modalVisible}
                    onCancel={() => setModalVisible(false)}
                    footer={[
                        <Button key="close" onClick={() => setModalVisible(false)}>
                            Close
                        </Button>
                    ]}
                    width={800}
                >
                    {viewingLead && (
                        <div className="space-y-4">
                            <Descriptions bordered column={2}>
                                <Descriptions.Item label="Lead ID">
                                    <Typography.Text code>#{viewingLead.id}</Typography.Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Status">
                                    <Tag color={statusColor(viewingLead.status)}>
                                        {viewingLead.status?.charAt(0).toUpperCase() + viewingLead.status?.slice(1)}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="Customer">
                                    {viewingLead.user?.name}
                                </Descriptions.Item>
                                <Descriptions.Item label="Email">
                                    {viewingLead.user?.email}
                                </Descriptions.Item>
                                <Descriptions.Item label="Vendor">
                                    {viewingLead.vendor?.business_name}
                                </Descriptions.Item>
                                <Descriptions.Item label="Phone">
                                    {viewingLead.contact_phone}
                                </Descriptions.Item>
                                <Descriptions.Item label="Event Type">
                                    {viewingLead.event_type}
                                </Descriptions.Item>
                                <Descriptions.Item label="Event Date">
                                    {viewingLead.event_date}
                                </Descriptions.Item>
                                <Descriptions.Item label="Event Time">
                                    {viewingLead.event_time}
                                </Descriptions.Item>
                                <Descriptions.Item label="Guest Count">
                                    {viewingLead.guest_count}
                                </Descriptions.Item>
                                <Descriptions.Item label="Venue">
                                    {viewingLead.venue}
                                </Descriptions.Item>
                                <Descriptions.Item label="Special Requirements" span={2}>
                                    {viewingLead.special_requirements || 'None'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Selected Dishes" span={2}>
                                    {viewingLead.selected_dishes ? (
                                        <div className="space-y-1">
                                            {viewingLead.selected_dishes.map((dish, index) => (
                                                <div key={index} className="flex justify-between">
                                                    <span>{dish.name || `Dish ${index + 1}`}</span>
                                                    <span className="text-gray-500">Qty: {dish.quantity || 1}</span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : 'None'}
                                </Descriptions.Item>
                            </Descriptions>
                        </div>
                    )}
                </Modal>

                {/* Lead Edit Modal */}
                <LeadEditModal
                    visible={editModalVisible}
                    lead={editingLead}
                    onClose={() => {
                        setEditModalVisible(false);
                        setEditingLead(null);
                    }}
                    onSave={handleLeadSave}
                    onSendQuote={handleSendQuote}
                />
            </div>
        </div>
    );
}

export default AdminLeads;
